'use client'
import React from 'react'
import {Box, Flex, rem, Stack, Text} from '@mantine/core'
import {createStyles} from '@mantine/styles'
import {
    IconAlertTriangle,
    IconBatteryCharging,
    IconBed,
    IconBrain,
    IconHeart,
    IconRefresh,
    IconShieldCog,
    IconUserHeart,
    IconWalk,
} from '@tabler/icons-react'
import {isMobile} from 'react-device-detect'
import {PersonFocusIcon} from './Icons/Labs/PersonFocusIcon'

const useStyles = createStyles((theme: any) => ({
    container: {
        // display: 'flex',
        // flexDirection: 'row',
        [theme.fn.smallerThan('md')]: {
            flexDirection: 'column',
        },
    },

    title: {
        fontSize: '42px',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '-2px',
        color: 'black',

        [theme.fn.smallerThan('md')]: {
            fontSize: rem(24),
        },
    },

    description: {
        fontSize: theme.fontSizes.md,
        lineHeight: 1.6,
        color: 'black',
        marginTop: theme.spacing.md,

        [theme.fn.smallerThan('md')]: {
            fontSize: theme.fontSizes.sm,
        },
    },

    leftSection: {
        flex: 1,
        marginBottom: theme.spacing.xl,
        maxWidth: '70%', // Adjusted width for the left section

        [theme.fn.smallerThan('md')]: {
            maxWidth: '100%',
        },
    },

    textContainer: {
        maxWidth: '80%', // Further constraining the text width within the left section

        [theme.fn.smallerThan('md')]: {
            maxWidth: '100%',
        },
    },

    rightSection: {
        flex: 1,
    },

    categoryCard: {
        padding: theme.spacing.sm,
        backgroundColor: '#E1DBEF',

        [theme.fn.smallerThan('md')]: {
            padding: theme.spacing.xs,
        },
    },

    circleIcon: {
        width: rem(40),
        height: rem(40),
        borderRadius: '50%',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        [theme.fn.smallerThan('md')]: {
            width: rem(32),
            height: rem(32),
        },
    },

    pink: {
        backgroundColor: '#FF69B4',
        color: 'white',
        fontWeight: 'bold',
        width: rem(24),
        height: rem(24),
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: rem(14),
    },
}))

interface HealthCategory {
    description: string
    icon: React.ReactNode
}

const LabWellnessDetails: React.FC = () => {
    const {classes} = useStyles()

    const title = 'Go deeper than your annual checkup'
    const description =
        'This specialized protocol, designed by physicians with decades of experience at top academic institutions, ' +
        'delivers a complete picture of your key health markers—for performance, sleep, energy, and long-term health.'


    const healthCategories: HealthCategory[] = [
        {
            description: 'Keep your heart healthy',
            icon: <IconHeart size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Improve your sleep & restfulness',
            icon: <IconBed size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Optimize your metabolic health',
            icon: <IconRefresh size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Boost & sustain your energy',
            icon: <IconBatteryCharging size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Enhance your mental clarity and mood',
            icon: <IconBrain size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Strengthen your immune resilience',
            icon: <IconShieldCog size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Spot early warning signs',
            icon: <IconAlertTriangle size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Support overall well-being',
            icon: <IconUserHeart size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
        {
            description: 'Live a longer, more active life',
            icon: <IconWalk size={38} stroke={1.5} color={'#2D1AFF'}/>,
        },
    ]

    return (
        <Flex className={classes.container}>
            <Box className={classes.leftSection}>
                <Stack gap={isMobile ? 'xs' : 'md'}>
                    {!isMobile && <PersonFocusIcon width={125} height={125}/>}
                    <Stack className={classes.textContainer} gap={13}>
                        {renderTitle()}
                        {renderSubtitle()}
                    </Stack>
                </Stack>
            </Box>

            <Box className={classes.rightSection}>
                {renderCategories()}
            </Box>
        </Flex>
    )

    function renderTitle() {
        return (
            <Text c={'var(--GM-Text-Black, #181818)'} fz={isMobile ? 36 : 48} fw={isMobile ? 500 : 400}
                  lh={isMobile ? '40px' : '54px'}
                  lts={isMobile ? '-0.72px' : '-1.08px'}>
                {title}
            </Text>)
    }


    function renderSubtitle() {
        return (
            <Text c={'var(--GM-Text-Black, #181818)'} fz={isMobile ? 18 : 24} fw={500} lh={'130%'}>
                {description}
            </Text>)
    }

    function renderCategories() {
        return <Stack gap={isMobile ? 12 : 'xs'}>
            {healthCategories.map((category: HealthCategory, index: number) => (
                renderCategory(category, index)
            ))}
        </Stack>

    }

    function renderCategory(category: HealthCategory, index: number) {
        const {description, icon} = category;
        return (
            <Flex gap={16} align={'center'} bg={'#E1DBEF'} p={isMobile ? 12 : 20} style={{borderRadius: 16}}
                  key={'category-' + index}>
                {icon}
                <Text c={'var(--GM-Text-Black, #181818)'} fz={isMobile ? 18 : 24} fw={500} lh={'30px'}
                      lts={isMobile ? '-0.36px' : '-0.48px'}>
                    {description}
                </Text>
            </Flex>
        )
    }
}

export default LabWellnessDetails
